import { useState, useEffect, useCallback } from "react";
import { t } from "@/lib/i18n";
import { CreditCard, ArrowSquareOut, DownloadSimple, CircleNotch, Sparkle } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { PageShell } from "@/components/PageShell";
import api, { errDetail } from "@/lib/api";
import { PromoCodeInput } from "@/components/PromoCodeInput";

const StatusBadge = ({ inv }) => {
  const map = {
    paid: "bg-emerald-50 text-emerald-700",
    failed: "bg-red-50 text-red-700",
    open: "bg-amber-50 text-amber-700",
  };
  const label = inv.status === "paid" ? t("bill.paid", "Paid") : inv.payable ? (inv.status === "failed" ? t("bill.failed", "Failed") : t("bill.open", "Open")) : inv.status;
  return <span className={`text-[11px] font-bold uppercase rounded px-2 py-0.5 ${map[inv.status] || "bg-gray-100 text-gray-500"}`}>{label}</span>;
};

export default function BillingPage() {
  const [info, setInfo] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [packages, setPackages] = useState([]);
  const [showPlans, setShowPlans] = useState(false);
  const [planTab, setPlanTab] = useState("personal");
  const [busy, setBusy] = useState("");
  const [pool, setPool] = useState(null);
  const [packs, setPacks] = useState([]);
  const [siUsage, setSiUsage] = useState([]);
  const [promoCode, setPromoCode] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);

  const packEligible = (p) => !promoInfo || promoInfo.kind !== "booster" || !promoInfo.allowed_pack_ids?.length || promoInfo.allowed_pack_ids.includes(p.id);
  const planEligible = (p) => !promoInfo || promoInfo.kind !== "package" || !promoInfo.allowed_package_ids?.length || promoInfo.allowed_package_ids.includes(p.id);

  const load = useCallback(() => {
    api.get("/billing/subscription").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/billing/invoices").then((r) => setInvoices(r.data.invoices || []))
      .catch((e) => { if (e.response?.status !== 403) toast.error(errDetail(e)); setInvoices([]); });
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/team/tokens").then((r) => setPool(r.data)).catch(() => {});
    api.get("/token-packages").then((r) => setPacks(r.data)).catch(() => {});
    api.get("/shared-inboxes").then((r) => {
      (r.data.shared_inboxes || []).forEach((s) =>
        api.get(`/shared-inboxes/${s.id}/usage`).then((u) =>
          setSiUsage((prev) => [...prev.filter((x) => x.id !== s.id), { id: s.id, name: s.name, color: s.color, ...u.data }])
        ).catch(() => {}));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const buyPack = async (pack) => {
    setBusy(pack.id);
    try {
      const { data } = await api.post("/billing/tokens/checkout", { pack_id: pack.id, origin_url: window.location.origin, code: promoCode || undefined });
      if (data.free) { toast.success(data.message); setPromoCode(null); load(); setBusy(""); }
      else window.location.href = data.url;
    } catch (e) { toast.error(errDetail(e)); setBusy(""); }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(errDetail(e)); setBusy(""); }
  };

  const changePlan = async (pkg) => {
    setBusy(pkg.id);
    try {
      const { data } = await api.post("/billing/checkout", { package_id: pkg.id, origin_url: window.location.origin, code: promoCode || undefined });
      if (data.free) { toast.success(data.message); setPromoCode(null); load(); setShowPlans(false); setBusy(""); }
      else window.location.href = data.url;
    } catch (e) { toast.error(errDetail(e)); setBusy(""); }
  };

  const sub = info?.subscription;
  const isPayer = info?.is_payer;
  const audienceOf = (p) => p.audience || (p.seats <= 1 ? "personal" : "team");
  const currentPkg = packages.find((p) => p.name === sub?.package_name);
  const currentAudience = currentPkg ? audienceOf(currentPkg) : (sub?.seats > 1 ? "team" : "personal");
  const tabPackages = packages.filter((p) => audienceOf(p) === planTab);
  useEffect(() => { if (sub && packages.length) setPlanTab(currentAudience); }, [sub?.package_name, packages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageShell title={t("bill.title", "Billing")} subtitle={t("bill.subtitle", "Your subscription, payment method and invoices.")} testId="billing-page">
      {info?.code_ending && (
        <div data-testid="code-expiry-banner" className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md px-4 py-3 mb-6 text-sm flex items-center gap-2 flex-wrap">
          <span>⏳</span>
          <span>{t("bill.code_ending", "Your access via code {code} ends on {date} — upgrade to keep access without interruption.", { code: info.code_ending.code, date: info.code_ending.date })}</span>
          <button data-testid="code-expiry-upgrade-btn" onClick={() => setShowPlans(true)} className="ml-auto font-semibold underline hover:no-underline">{t("bill.see_plans", "See plans")}</button>
        </div>
      )}
      <div data-testid="billing-plan-card" className="border border-gray-200 rounded-md p-5 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-lg text-gray-900 capitalize">{sub?.package_name || info?.plan || "Free"} {t("bill.plan_word", "plan")}</span>
              {sub?.status === "past_due" && <span className="text-[11px] font-bold uppercase bg-red-50 text-red-700 rounded px-2 py-0.5">{t("bill.payment_failed", "payment failed")}</span>}
              {sub?.status === "canceled" && <span className="text-[11px] font-bold uppercase bg-gray-100 text-gray-500 rounded px-2 py-0.5">{t("bill.canceled", "canceled")}</span>}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {sub?.price_monthly ? t("bill.price_line", "${price}/month · {seats} seat(s)", { price: sub.price_monthly, seats: sub.seats }) : t("bill.no_sub", "No paid subscription")}
              {sub?.current_period_end && ` · ${sub.cancel_at_period_end || sub.status === "canceled" ? t("bill.ends", "ends") : t("bill.renews", "renews")} ${dayjs(sub.current_period_end).format("MMM D, YYYY")}`}
            </div>
          </div>
          {isPayer && (
            <div className="flex gap-2">
              <button data-testid="manage-billing-btn" onClick={openPortal} disabled={busy === "portal"}
                className="flex items-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-800 rounded-md px-4 py-2 text-sm font-semibold transition-colors">
                {busy === "portal" ? <CircleNotch size={14} className="animate-spin" /> : <CreditCard size={15} />} {t("bill.manage", "Manage billing")}
              </button>
              <button data-testid="change-plan-btn" onClick={() => setShowPlans(!showPlans)}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-semibold transition-colors">
                <Sparkle size={15} /> {t("bill.change_plan", "Change plan")}
              </button>
            </div>
          )}
        </div>
        {!isPayer && <p className="text-xs text-gray-400 mt-3">{t("bill.managed_by_admin", "Billing for your team is managed by your team admin.")}</p>}
      </div>

      {pool && (
        <div data-testid="token-pool-card" className="border border-gray-200 rounded-md p-5 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <h2 className="font-heading font-bold text-gray-900">{t("bill.token_pool", "Token pool")}{pool.period_end ? t("bill.pool_resets", " — resets {date}", { date: dayjs(pool.period_end).format("MMM D, YYYY") }) : ""}</h2>
            {pool.locked_model && <span className="text-[11px] bg-gray-100 text-gray-600 rounded px-2 py-0.5">{t("bill.model_locked", "🔒 model locked to {model}", { model: pool.locked_model.model })}</span>}
          </div>
          {pool.unlimited ? (
            <p data-testid="token-pool-unlimited" className="text-sm text-gray-600">{t("bill.consumed", "Consumed {n} tokens this period", { n: Math.round(pool.consumed).toLocaleString() })} · <b className="text-emerald-700">{t("bill.unlimited", "Unlimited")}</b> {t("bill.on_plan", "on your plan")}</p>
          ) : (
            <>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div className={`h-full rounded-full ${pool.pct_used >= 100 ? "bg-red-500" : pool.pct_used >= 80 ? "bg-amber-500" : "bg-blue-500"}`} style={{ width: `${Math.min(100, pool.pct_used)}%` }} />
              </div>
              <p data-testid="token-pool-stats" className="text-sm text-gray-600">
                {t("bill.pool_stats", "{used} / {total} used · Included: {inc} · Purchased: +{pur} · Remaining:", { used: Math.round(pool.consumed).toLocaleString(), total: pool.total.toLocaleString(), inc: (pool.allocated || 0).toLocaleString(), pur: pool.purchased_extra.toLocaleString() })} <b>{Math.round(pool.remaining).toLocaleString()}</b>
              </p>
            </>
          )}
          {isPayer && packs.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="buy-tokens-row">
              {packs.map((p) => (
                <button key={p.id} data-testid={`buy-pack-${p.id}`} onClick={() => buyPack(p)} disabled={busy === p.id || !packEligible(p)}
                  title={!packEligible(p) ? t("promo.not_for_pack", "Your code doesn't apply to this pack") : undefined}
                  className="border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-md px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy === p.id ? "..." : `💳 ${p.name} — ${p.tokens.toLocaleString()} ${t("bill.tokens_word", "tokens")} · $${p.price_usd}`}{!packEligible(p) && " 🚫"}
                </button>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-2">{t("bill.tokens_expire", "Unused tokens expire at reset.")}</p>
          {isPayer && (
            <div className="mt-3">
              <PromoCodeInput kind="booster" onApplied={(c, d) => { setPromoCode(c); setPromoInfo(d); }} testId="promo-booster" />
              {promoInfo?.kind === "booster" && promoInfo.allowed_pack_ids?.length > 0 && (
                <p data-testid="promo-booster-scope-note" className="text-[11px] text-amber-700 mt-1.5">
                  {t("promo.pack_scope", "This code only applies to:")} {packs.filter((p) => promoInfo.allowed_pack_ids.includes(p.id)).map((p) => p.name).join(", ")}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {showPlans && (
        <div data-testid="billing-plans-wrap" className="mb-6">
          <div className="mb-3">
            <PromoCodeInput kind="package" onApplied={(c, d) => { setPromoCode(c); setPromoInfo(d); }} testId="promo-plan" />
            {promoInfo?.kind === "package" && promoInfo.allowed_package_ids?.length > 0 && (
              <p data-testid="promo-plan-scope-note" className="text-[11px] text-amber-700 mt-1.5">
                {t("promo.plan_scope", "This code only applies to:")} {packages.filter((p) => promoInfo.allowed_package_ids.includes(p.id)).map((p) => p.name).join(", ")}
              </p>
            )}
          </div>
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-1 mb-4" data-testid="billing-plan-tabs">
            <button data-testid="billing-tab-personal" onClick={() => setPlanTab("personal")}
              className={`px-4 py-1.5 text-sm font-semibold rounded ${planTab === "personal" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}>{t("bill.personal_plans", "Personal plans")}</button>
            <button data-testid="billing-tab-team" onClick={() => setPlanTab("team")}
              className={`px-4 py-1.5 text-sm font-semibold rounded ${planTab === "team" ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}>{t("bill.team_plans", "Team plans")}</button>
          </div>
          {planTab === "team" && currentAudience === "personal" && (
            <p data-testid="billing-note-to-team" className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mb-3">
              {t("bill.note_to_team", "Switching to a team plan makes you a team admin — you'll get seats and can invite members.")}</p>
          )}
          {planTab === "personal" && currentAudience === "team" && (
            <p data-testid="billing-note-to-personal" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
              {t("bill.note_to_personal", "Moving to a personal plan reduces you to 1 seat — remove your team members first.")}</p>
          )}
          <div data-testid="billing-plans" className="grid sm:grid-cols-3 gap-3">
          {tabPackages.map((p) => (
            <div key={p.id} className={`border rounded-md p-4 ${p.name === sub?.package_name ? "border-blue-400 bg-blue-50/40" : "border-gray-200"}`}>
              <div className="font-heading font-bold text-gray-900">{p.name}</div>
              <div className="text-sm text-gray-500 mb-3">{t("bill.plan_price", "${price}/mo · {seats} seat(s)", { price: p.price_monthly, seats: p.seats })}</div>
              <button data-testid={`select-plan-${p.id}`} onClick={() => changePlan(p)}
                disabled={busy === p.id || p.name === sub?.package_name || !planEligible(p)}
                title={!planEligible(p) ? t("promo.not_for_plan", "Your code doesn't apply to this plan") : undefined}
                className="w-full text-sm font-semibold rounded-md py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {busy === p.id ? "..." : p.name === sub?.package_name ? t("bill.current_plan", "Current plan") : !planEligible(p) ? t("promo.code_na", "Code not valid here") : t("bill.switch", "Switch")}
              </button>
            </div>
          ))}
          {tabPackages.length === 0 && (
            <p data-testid="billing-empty-tab" className="text-sm text-gray-500 sm:col-span-3">
              {t("bill.no_plans", "No {tab} plans published yet —", { tab: planTab === "personal" ? t("bill.personal_word", "personal") : t("bill.team_word", "team") })}{" "}
              <button onClick={() => setPlanTab(planTab === "personal" ? "team" : "personal")} className="text-blue-600 underline">
                {t("bill.see_other", "see the {tab} plans →", { tab: planTab === "personal" ? t("bill.team_word", "team") : t("bill.personal_word", "personal") })}</button>
            </p>
          )}
          </div>
        </div>
      )}

      {isPayer && (
        <>
          <h2 className="font-heading font-bold text-gray-900 mb-3">{t("bill.invoices", "Invoices")}</h2>
          {invoices === null ? (
            <div className="text-sm text-gray-400 py-6 text-center"><CircleNotch size={18} className="animate-spin inline" /> {t("bill.loading_invoices", "Loading invoices...")}</div>
          ) : invoices.length === 0 ? (
            <div data-testid="invoices-empty" className="border border-dashed border-gray-300 rounded-md p-8 text-center text-sm text-gray-400">
              {t("bill.no_invoices", "No invoices yet — invoices appear after your first subscription payment.")}
            </div>
          ) : (
            <div data-testid="invoices-table" className="border border-gray-200 rounded-md overflow-hidden">
              <div className="grid grid-cols-[1fr_1fr_100px_90px_170px] gap-2 px-4 py-2 bg-gray-50 text-[11px] font-bold uppercase text-gray-500">
                <span>{t("bill.date", "Date")}</span><span>{t("bill.invoice_no", "Invoice #")}</span><span>{t("bill.amount", "Amount")}</span><span>{t("bill.status", "Status")}</span><span>{t("bill.action", "Action")}</span>
              </div>
              {invoices.map((inv) => (
                <div key={inv.number} data-testid={`invoice-row-${inv.number}`}
                  className={`grid grid-cols-[1fr_1fr_100px_90px_170px] gap-2 px-4 py-3 border-t border-gray-100 text-sm items-center ${inv.payable ? "bg-red-50/50" : ""}`}>
                  <span className="text-gray-700">{dayjs(inv.date).format("MMM D, YYYY")}</span>
                  <span className="text-gray-500 truncate">{inv.number}</span>
                  <span className="font-semibold text-gray-900">${inv.amount.toFixed(2)}</span>
                  <StatusBadge inv={inv} />
                  <span className="flex items-center gap-2">
                    {inv.payable && (
                      <a data-testid={`invoice-pay-${inv.number}`} href={inv.hosted_url} target="_blank" rel="noreferrer"
                        className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded px-3 py-1.5 transition-colors">{t("bill.pay", "💳 Pay")}</a>
                    )}
                    {inv.hosted_url && (
                      <a data-testid={`invoice-view-${inv.number}`} href={inv.hosted_url} target="_blank" rel="noreferrer"
                        className="flex items-center gap-0.5 text-xs text-blue-600 hover:underline"><ArrowSquareOut size={12} /> {t("bill.view", "View")}</a>
                    )}
                    {inv.pdf_url && !inv.payable && (
                      <a data-testid={`invoice-download-${inv.number}`} href={inv.pdf_url}
                        className="flex items-center gap-0.5 text-xs text-gray-600 hover:text-gray-900"><DownloadSimple size={12} /> {t("bill.download", "Download")}</a>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {siUsage.length > 0 && (
        <div data-testid="si-usage-card" className="bg-white border border-gray-200 rounded-lg p-5 mt-6">
          <h2 className="font-heading font-bold text-gray-900 mb-3">{t("bill.si_usage", "Shared inbox usage")}</h2>
          {siUsage.map((s) => (
            <div key={s.id} data-testid={`si-usage-row-${s.id}`} className="flex items-center justify-between py-2 border-b border-gray-50 text-sm">
              <span className="flex items-center gap-2 text-gray-700">
                <span className="w-2 h-2 rounded-full" style={{ background: s.color || "#2563eb" }} />{s.name}
              </span>
              <span data-testid={`si-usage-tokens-${s.id}`} className="font-semibold text-gray-900">
                {Math.round(s.total_tokens || 0).toLocaleString()} {t("bill.tokens", "tokens")}
              </span>
            </div>
          ))}
          <p className="text-[11px] text-gray-400 mt-2">{t("bill.si_note", "All shared inbox AI activity is billed to your team token pool.")}</p>
        </div>
      )}
    </PageShell>
  );
}
